import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { LuChevronDown, LuSearch, LuShieldCheck } from "react-icons/lu";
import {
  SA_PERMISSION_ACTION_LABELS,
  SA_PLATFORM_PERMISSION_GROUPS,
  createEmptyPermissionMatrix,
} from "../../data/superAdminAccessCatalog";
import {
  accessExtractMessage,
  createAccessRole,
  fetchAccessRoleById,
  updateAccessRole,
} from "../../services/superAdminAccess.service";
import "../../assets/styles/super_admin_access.scss";

const SUPER_ADMIN_ROLE_CATEGORY_OPTIONS = [
  { value: "platform", label: "Platform Role" },
  { value: "support_manager", label: "Support Manager Role" },
  { value: "logistics_manager", label: "Logistics Manager Role" },
];

const SUPPORT_MANAGER_ROLE_CATEGORY_OPTIONS = [
  { value: "support_user", label: "Support User Role" },
];

const LOGISTICS_MANAGER_ROLE_CATEGORY_OPTIONS = [
  { value: "logistics_user", label: "Logistics User Role" },
];

const mergePermissions = (saved = {}) => {
  const base = createEmptyPermissionMatrix();
  Object.entries(saved).forEach(([resourceId, actions]) => {
    if (!base[resourceId] || !actions || typeof actions !== "object") return;
    Object.entries(actions).forEach(([action, enabled]) => {
      if (Object.prototype.hasOwnProperty.call(base[resourceId], action)) {
        base[resourceId][action] = Boolean(enabled);
      }
    });
  });
  return base;
};

export default function SuperAdminCreateRolePage({ basePath = "/super-admin" }) {
  const navigate = useNavigate();
  const { roleId } = useParams();
  const isEditMode = Boolean(roleId);
  const isSupportManager = basePath.includes("/support-manager");
  const isLogisticsManager = basePath.includes("/logistics-manager");
  const permissionGroups = useMemo(
    () => {
      if (isSupportManager) {
        return SA_PLATFORM_PERMISSION_GROUPS.filter((group) => group.id === "support");
      }
      if (isLogisticsManager) {
        return SA_PLATFORM_PERMISSION_GROUPS.filter((group) => group.id === "logistics");
      }
      return SA_PLATFORM_PERMISSION_GROUPS;
    },
    [isLogisticsManager, isSupportManager],
  );

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [scope, setScope] = useState("platform");
  const [roleLevel, setRoleLevel] = useState(
    isSupportManager ? "support_user" : isLogisticsManager ? "logistics_user" : "platform",
  );
  const [status, setStatus] = useState("Active");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [roleType, setRoleType] = useState("Custom");
  const [expandedGroups, setExpandedGroups] = useState(
    () => new Set(["access_control", "platform", "master_data", "content"]),
  );
  const roleCategoryOptions = useMemo(() => {
    if (isSupportManager) return SUPPORT_MANAGER_ROLE_CATEGORY_OPTIONS;
    if (isLogisticsManager) return LOGISTICS_MANAGER_ROLE_CATEGORY_OPTIONS;
    return SUPER_ADMIN_ROLE_CATEGORY_OPTIONS;
  }, [isLogisticsManager, isSupportManager]);
  const [permissions, setPermissions] = useState(createEmptyPermissionMatrix);

  useEffect(() => {
    if (!isEditMode) return undefined;

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const role = await fetchAccessRoleById(roleId);
        if (cancelled) return;
        if (!role) {
          toast.error("Role not found.");
          navigate(`${basePath}/roles`);
          return;
        }
        setName(role.name || "");
        setDescription(role.description || "");
        setScope(role.scope || "platform");
        setRoleLevel(
          role.roleLevel ||
            (isSupportManager ? "support_user" : isLogisticsManager ? "logistics_user" : "platform"),
        );
        setStatus(role.status || "Active");
        setRoleType(role.type || "Custom");
        setPermissions(mergePermissions(role.permissions || {}));

        const groupsWithGrants = new Set();
        permissionGroups.forEach((group) => {
          const hasGrant = group.resources.some((resource) =>
            resource.actions.some(
              (action) => role.permissions?.[resource.id]?.[action],
            ),
          );
          if (hasGrant) groupsWithGrants.add(group.id);
        });
        if (groupsWithGrants.size) {
          setExpandedGroups(groupsWithGrants);
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(accessExtractMessage(error, "Failed to load role."));
          navigate(`${basePath}/roles`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [basePath, isEditMode, navigate, permissionGroups, roleId]);

  const togglePermission = (resourceId, action) => {
    setPermissions((current) => ({
      ...current,
      [resourceId]: {
        ...current[resourceId],
        [action]: !current[resourceId]?.[action],
      },
    }));
  };

  const setResourcesPermission = (resources, checked) => {
    setPermissions((current) => {
      const next = { ...current };
      resources.forEach((resource) => {
        next[resource.id] = Object.fromEntries(
          resource.actions.map((action) => [action, checked]),
        );
      });
      return next;
    });
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups((current) => {
      const next = new Set(current);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const filteredGroups = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return permissionGroups;

    return permissionGroups.map((group) => ({
      ...group,
      resources: group.resources.filter((resource) =>
        `${group.label} ${group.description} ${resource.label} ${resource.actions
          .map((action) => SA_PERMISSION_ACTION_LABELS[action] || action)
          .join(" ")}`
          .toLowerCase()
          .includes(normalizedQuery),
      ),
    })).filter((group) => group.resources.length > 0);
  }, [permissionGroups, query]);

  const permissionSummary = useMemo(() => {
    let selected = 0;
    let total = 0;
    permissionGroups.forEach((group) => {
      group.resources.forEach((resource) => {
        resource.actions.forEach((action) => {
          total += 1;
          if (permissions[resource.id]?.[action]) selected += 1;
        });
      });
    });
    return { selected, total };
  }, [permissionGroups, permissions]);

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!name.trim()) {
      toast.error("Please enter a role name.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        ...(!isEditMode ? { roleLevel } : {}),
        scope,
        status,
        tone: "orange",
        permissions,
      };

      if (isEditMode) {
        await updateAccessRole(roleId, payload);
        toast.success("Role updated successfully.");
      } else {
        await createAccessRole(payload);
        toast.success("Role saved successfully.");
      }
      navigate(`${basePath}/roles`);
    } catch (error) {
      toast.error(
        accessExtractMessage(
          error,
          isEditMode ? "Failed to update role." : "Failed to save role.",
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="sa-access-page">
        <div className="sa-access-empty">Loading role...</div>
      </div>
    );
  }

  return (
    <div className="sa-access-page">
      <nav className="sa-access-breadcrumb" aria-label="Breadcrumb">
        <Link to={`${basePath}/roles`}>Role Management</Link>
        <span aria-hidden>&gt;</span>
        <span aria-current="page">{isEditMode ? "Edit Role" : "Create Role"}</span>
      </nav>

      <header className="sa-access-head">
        <div>
          <h1>{isEditMode ? "Edit Role" : "Create Role"}</h1>
          <p>
            {isEditMode
              ? "Update role details and platform permissions."
              : "Define a platform role and grant resource-level permissions."}
          </p>
        </div>
        <div className="sa-access-actions">
          <button
            type="button"
            className="sa-access-btn sa-access-btn--ghost"
            onClick={() => navigate(`${basePath}/roles`)}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="sa-create-role-form"
            className="sa-access-btn sa-access-btn--primary"
            disabled={saving}
          >
            {saving ? "Saving..." : isEditMode ? "Update Role" : "Save Role"}
          </button>
        </div>
      </header>

      <form
        id="sa-create-role-form"
        className={isLogisticsManager ? "sa-user-form--pill-inputs" : undefined}
        onSubmit={onSubmit}
      >
        <section className="sa-access-section">
          <h2>1. Role Details</h2>
          <div className="sa-access-grid sa-access-grid--three">
            <label className="sa-access-field">
              <span>
                Role Name <em>*</em>
              </span>
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Enter role name"
                required
                disabled={isEditMode && roleType === "System"}
              />
            </label>
            <label className="sa-access-field">
              <span>Description</span>
              <input
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Enter role description"
              />
            </label>
            <label className="sa-access-field">
              <span>
                Role Category <em>*</em>
              </span>
              <select
                value={roleLevel}
                onChange={(event) => setRoleLevel(event.target.value)}
                disabled={isEditMode}
              >
                {roleCategoryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="sa-access-field">
              <span>
                Access Scope <em>*</em>
              </span>
              <select value={scope} onChange={(event) => setScope(event.target.value)}>
                <option value="platform">Platform-wide</option>
                <option value="module">Selected modules</option>
                <option value="read_only">Read-only administration</option>
              </select>
            </label>
            {isEditMode ? (
              <label className="sa-access-field">
                <span>Status</span>
                <select
                  value={status}
                  onChange={(event) => setStatus(event.target.value)}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </label>
            ) : null}
          </div>
        </section>

        <section className="sa-access-section">
          <div className="sa-permission-heading">
            <div>
              <h2>2. Platform Permission Matrix</h2>
              <p>Select only the resources and operations this role requires.</p>
            </div>
            <div className="sa-permission-count" aria-live="polite">
              <LuShieldCheck aria-hidden />
              <strong>{permissionSummary.selected}</strong>
              <span>of {permissionSummary.total} permissions selected</span>
            </div>
          </div>

          <div className="sa-permission-toolbar">
            <label className="sa-permission-search">
              <LuSearch aria-hidden />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search module, resource or action (e.g. Bulk Import)"
                aria-label="Search permissions"
              />
            </label>
            <button
              type="button"
              className="sa-access-btn sa-access-btn--ghost"
              onClick={() =>
                setResourcesPermission(
                  permissionGroups.flatMap((group) => group.resources),
                  permissionSummary.selected !== permissionSummary.total,
                )
              }
            >
              {permissionSummary.selected === permissionSummary.total
                ? "Clear all"
                : "Select all"}
            </button>
          </div>

          <div className="sa-permission-groups">
            {filteredGroups.map((group) => {
              const isExpanded = Boolean(query.trim()) || expandedGroups.has(group.id);
              const groupActions = group.resources.flatMap((resource) =>
                resource.actions.map((action) => ({ resource, action })),
              );
              const selectedCount = groupActions.filter(
                ({ resource, action }) => permissions[resource.id]?.[action],
              ).length;
              const allSelected =
                groupActions.length > 0 && selectedCount === groupActions.length;

              return (
                <article className="sa-permission-group" key={group.id}>
                  <div className="sa-permission-group__head">
                    <button
                      type="button"
                      className="sa-permission-group__toggle"
                      onClick={() => toggleGroup(group.id)}
                      aria-expanded={isExpanded}
                    >
                      <LuChevronDown
                        className={isExpanded ? "is-expanded" : ""}
                        aria-hidden
                      />
                      <span>
                        <strong>{group.label}</strong>
                        <small>{group.description}</small>
                      </span>
                    </button>
                    <div className="sa-permission-group__summary">
                      <span>
                        {selectedCount}/{groupActions.length}
                      </span>
                      <label>
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={(node) => {
                            if (node) {
                              node.indeterminate =
                                selectedCount > 0 && !allSelected;
                            }
                          }}
                          onChange={(event) =>
                            setResourcesPermission(
                              group.resources,
                              event.target.checked,
                            )
                          }
                        />
                        All
                      </label>
                    </div>
                  </div>

                  {isExpanded ? (
                    <div className="sa-permission-resources">
                      {group.resources.map((resource) => {
                        const resourceSelected = resource.actions.filter(
                          (action) => permissions[resource.id]?.[action],
                        ).length;
                        const resourceAll =
                          resourceSelected === resource.actions.length;

                        return (
                          <div className="sa-permission-resource" key={resource.id}>
                            <div className="sa-permission-resource__name">
                              <strong>{resource.label}</strong>
                              <label>
                                <input
                                  type="checkbox"
                                  checked={resourceAll}
                                  ref={(node) => {
                                    if (node) {
                                      node.indeterminate =
                                        resourceSelected > 0 && !resourceAll;
                                    }
                                  }}
                                  onChange={(event) =>
                                    setResourcesPermission(
                                      [resource],
                                      event.target.checked,
                                    )
                                  }
                                />
                                Select resource
                              </label>
                            </div>
                            <div className="sa-permission-actions">
                              {resource.actions.map((action) => (
                                <label
                                  className="sa-permission-action"
                                  key={action}
                                >
                                  <input
                                    type="checkbox"
                                    checked={!!permissions[resource.id]?.[action]}
                                    onChange={() =>
                                      togglePermission(resource.id, action)
                                    }
                                  />
                                  <span>
                                    {SA_PERMISSION_ACTION_LABELS[action] || action}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>

          {filteredGroups.length === 0 ? (
            <div className="sa-access-empty">No matching permissions found.</div>
          ) : null}
        </section>

        <div className="sa-access-form-footer">
          <button
            type="button"
            className="sa-access-btn sa-access-btn--ghost"
            onClick={() => navigate(`${basePath}/roles`)}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="sa-access-btn sa-access-btn--primary"
            disabled={saving}
          >
            {saving ? "Saving..." : isEditMode ? "Update Role" : "Save Role"}
          </button>
        </div>
      </form>
    </div>
  );
}
